import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, Signal } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { DrawerComponent, ZagDateTimePipe, ZagIcon } from '@zag/ui';
import type { DrawerMode } from '@zag/ui';
import type { EntityChange } from '@zag/core/models';
import { AuditService } from '../services/audit.service';

/** Represents a single changed field in an audit diff */
export interface AuditDiffEntry {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

/** Maps an operation to a Material Symbols icon name */
function operationIcon(operation: string): string {
  switch (operation) {
    case 'INSERT':
      return 'add-circle';
    case 'DELETE':
      return 'delete';
    default:
      return 'edit';
  }
}

/** Maps operation to translation key */
function operationLabelKey(operation: EntityChange['operation']): string {
  switch (operation) {
    case 'INSERT':
      return 'audit.panel.inserted';
    case 'DELETE':
      return 'audit.panel.deleted';
    default:
      return 'audit.panel.updated';
  }
}

/** Converts diff values to readable strings */
function formatDiffValue(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return `${value}`;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
}

/** Extracts a human-readable diff from two JSONB snapshots */
function extractDiff(
  operation: string,
  oldData: Record<string, unknown> | null | undefined,
  newData: Record<string, unknown> | null | undefined
): AuditDiffEntry[] {
  if (operation === 'INSERT') {
    const data = newData ?? {};
    return Object.entries(data).map(([field, value]) => ({
      field,
      oldValue: null,
      newValue: formatDiffValue(value),
    }));
  }

  if (operation === 'DELETE') {
    const data = oldData ?? {};
    return Object.entries(data).map(([field, value]) => ({
      field,
      oldValue: formatDiffValue(value),
      newValue: null,
    }));
  }

  // UPDATE: only show changed fields
  const old = oldData ?? {};
  const next = newData ?? {};
  const allKeys = new Set([...Object.keys(old), ...Object.keys(next)]);
  const entries: AuditDiffEntry[] = [];

  for (const field of allKeys) {
    const oldVal = old[field];
    const newVal = next[field];
    const oldStr = formatDiffValue(oldVal);
    const newStr = formatDiffValue(newVal);
    if (oldStr !== newStr) {
      entries.push({ field, oldValue: oldStr, newValue: newStr });
    }
  }

  return entries;
}

/**
 * Audit Log Panel Component
 *
 * A slide-in panel (using zag-drawer) that displays the full audit history
 * for a specific entity record. Shows a timeline of operations (INSERT, UPDATE,
 * DELETE) with field-level diffs for each change.
 *
 * Renders in push mode on ≥1440px screens (auto-open by default) and overlay
 * mode on smaller screens. The parent component controls `open`/`openChange`.
 *
 * @example
 * ```html
 * <zag-audit-log-panel
 *   schema="core"
 *   table="persons"
 *   [recordId]="personId()!"
 *   [open]="auditPanelOpen()"
 *   (openChange)="auditPanelOpen.set($event)"
 * />
 * ```
 */
@Component({
  selector: 'zag-audit-log-panel',
  imports: [DrawerComponent, ZagDateTimePipe, ZagIcon, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './audit-log-panel.component.html',
  styleUrl: './audit-log-panel.component.scss',
})
export class AuditLogPanelComponent {
  private readonly auditService = inject(AuditService);
  private readonly translate = inject(TranslateService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  // ============================================================================
  // Inputs / Outputs
  // ============================================================================

  /** Database schema name (e.g., 'core', 'planning', 'auth') */
  readonly schema = input.required<string>();

  /** Database table name (e.g., 'persons', 'teams') */
  readonly table = input.required<string>();

  /** Entity primary key value */
  readonly recordId = input.required<string>();

  /** Whether the drawer is open */
  readonly open = input<boolean>(false);

  /** Emitted when the drawer open state changes */
  readonly openChange = output<boolean>();

  // ============================================================================
  // Responsive Drawer Mode
  // ============================================================================

  /** At >= 1200px use push mode, below 1200px use overlay mode */
  private readonly isWideScreen = toSignal(this.breakpointObserver.observe('(min-width: 1200px)').pipe(map(result => result.matches)), {
    initialValue: false,
  });

  /** Computed drawer mode: push on wide screens, overlay on narrower viewports */
  readonly drawerMode = computed<DrawerMode>(() => (this.isWideScreen() ? 'push' : 'overlay'));

  // ============================================================================
  // Data Loading
  // ============================================================================

  private readonly entityParams: Signal<{ schema: string; table: string; recordId: string } | null> = computed(() => {
    const schema = this.schema();
    const table = this.table();
    const recordId = this.recordId();
    if (!recordId) return null;
    return { schema, table, recordId };
  });

  private readonly changesResource = this.auditService.createEntityResource(this.entityParams);

  readonly isLoading = computed(() => this.changesResource.isLoading());
  readonly hasError = computed(() => !!this.changesResource.error());

  readonly changes = computed(() => this.changesResource.value()?.data ?? []);

  /** Expanded change IDs (details are collapsed by default) */
  private readonly expandedChangeIds = signal<Set<number>>(new Set());

  // ============================================================================
  // Diff Data
  // ============================================================================

  readonly diffEntries = computed<Map<number, AuditDiffEntry[]>>(() => {
    const map = new Map<number, AuditDiffEntry[]>();
    for (const change of this.changes()) {
      map.set(change.id, extractDiff(change.operation, change.oldData, change.newData));
    }
    return map;
  });

  // ============================================================================
  // Helpers
  // ============================================================================

  getDiffEntries(changeId: number): AuditDiffEntry[] {
    return this.diffEntries().get(changeId) ?? [];
  }

  isExpanded(changeId: number): boolean {
    return this.expandedChangeIds().has(changeId);
  }

  toggleExpanded(changeId: number): void {
    this.expandedChangeIds.update(current => {
      const next = new Set(current);
      if (next.has(changeId)) {
        next.delete(changeId);
      } else {
        next.add(changeId);
      }
      return next;
    });
  }

  operationLabel(change: EntityChange): string {
    return this.translate.instant(operationLabelKey(change.operation)) as string;
  }

  operationIcon(change: EntityChange): string {
    return operationIcon(change.operation);
  }

  actor(change: EntityChange): string {
    return change.changedByName ?? change.changedBy ?? (this.translate.instant('audit.panel.unknownActor') as string);
  }

  close(): void {
    this.openChange.emit(false);
  }
}
